import React from "react";
import { 
  Hourglass, 
  MapPin, 
  Lock, 
  Wrench, 
  CheckCircle2, 
  Zap
} from "lucide-react";
import { useTrackerSummary, TrackerSummary } from "@/queries/tracker/useTrackerSummary";

const STAGES = [
  {
    key: "depositReceived",
    title: "Deposit received",
    description: "Your SolarCloud journey begins.",
    icon: Hourglass,
    color: "text-amber-400 border-amber-400/30 bg-amber-500/10",
  },
  {
    key: "siteLocated",
    title: "Site located",
    description: "A suitable site has been identified for your solar panels.",
    icon: MapPin,
    color: "text-blue-400 border-blue-400/30 bg-blue-500/10",
  },
  {
    key: "siteLockedIn",
    title: "Site locked in",
    description: "Feasibility approved and balance payment will now be taken.",
    icon: Lock,
    color: "text-indigo-400 border-indigo-400/30 bg-indigo-500/10",
  },
  {
    key: "installCommencing",
    title: "Install commences",
    description: "Deploying your solar panels.",
    icon: Wrench,
    color: "text-orange-400 border-orange-400/30 bg-orange-500/10",
  },
  {
    key: "installComplete",
    title: "Install complete",
    description: "Solar panels are set up and ready for testing.",
    icon: CheckCircle2,
    color: "text-teal-400 border-teal-400/30 bg-teal-500/10",
  },
  {
    key: "fullSystemActive",
    title: "Full system active",
    description: "Testing is complete and monitoring will now begin.",
    icon: Zap,
    color: "text-emerald-400 border-emerald-400/30 bg-emerald-500/10",
  },
  {
    key: "readyToGo",
    title: "Your solar is ready to go!",
    description: "Solar panels are now connected and generating green electricity.",
    icon: CheckCircle2,
    color: "text-teal-400 border-teal-400/30 bg-teal-500/10",
  },
];

export default function OrderTracker() {
  const { data, isLoading, error } = useTrackerSummary();

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
        <p className="text-brand-neutral-400 text-sm">Loading tracker details...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center max-w-lg mx-auto my-12">
        <p className="text-red-400 font-medium">Failed to load order tracker.</p>
        <p className="text-red-300/70 text-xs mt-1">Please try refreshing the page.</p>
      </div>
    );
  }

  // In Progress: sum of all milestones except readyToGo
  const inProgressPanels = STAGES
    .filter((s) => s.key !== "readyToGo")
    .reduce((acc, stage) => {
      const detail = data[stage.key as keyof TrackerSummary];
      return acc + (detail?.panelCount || 0);
    }, 0);

  // Active: panels in readyToGo
  const activePanels = data.readyToGo?.panelCount || 0;

  // Total: In Progress + Active
  const totalPanels = inProgressPanels + activePanels;

  return (
    <div className="space-y-12 max-w-5xl mx-auto p-4 md:p-8 bg-[#1a1f2c]/50 rounded-2xl border border-neutral-800 backdrop-blur-md">
      {/* Header with Circular Badges */}
      <div className="flex flex-col items-center justify-center space-y-8 pb-6 border-b border-neutral-800">
        <h1 className="text-3xl font-bold text-white tracking-tight text-center">Order Tracker</h1>
        
        <div className="relative flex items-center justify-between w-full max-w-lg px-4 md:px-8 py-2">
          {/* Connecting Horizontal Line */}
          <div className="absolute left-16 right-16 top-[60%] -translate-y-1/2 h-0.5 bg-neutral-700" />
          
          {/* In Progress Status */}
          <div className="relative flex flex-col items-center space-y-3 z-10">
            <span className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider">In Progress</span>
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-black border-2 border-white flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg shadow-black/50">
              {inProgressPanels}
            </div>
          </div>

          {/* Active Status */}
          <div className="relative flex flex-col items-center space-y-3 z-10">
            <span className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider">Active</span>
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#4fd1c5] border-2 border-[#4fd1c5] flex items-center justify-center text-black text-2xl md:text-3xl font-bold shadow-lg shadow-[#4fd1c5]/25">
              {activePanels}
            </div>
          </div>

          {/* Total Status */}
          <div className="relative flex flex-col items-center space-y-3 z-10">
            <span className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider">Total</span>
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#fbbf24] border-2 border-[#fbbf24] flex items-center justify-center text-black text-2xl md:text-3xl font-bold shadow-lg shadow-[#fbbf24]/25">
              {totalPanels}
            </div>
          </div>
        </div>
      </div>

      {/* Vertical Timeline */}
      <div className="relative pl-12 md:pl-20 space-y-10">
        {/* Continuous Timeline Vertical Line */}
        <div className="absolute left-6 md:left-[2.5rem] top-6 bottom-6 w-0.5 bg-neutral-700" />

        {STAGES.map((stage) => {
          const detail = data[stage.key as keyof TrackerSummary] || { panelCount: 0, sites: [] };
          const hasCount = detail.panelCount > 0;
          const isReadyToGo = stage.key === "readyToGo";

          // Node styling based on count and active status
          let nodeStyles = "bg-black border-neutral-600 text-white";
          if (hasCount) {
            if (isReadyToGo) {
              nodeStyles = "bg-[#4fd1c5] border-[#4fd1c5] text-black shadow-md shadow-[#4fd1c5]/20";
            } else {
              nodeStyles = "bg-[#fbbf24] border-[#fbbf24] text-black shadow-md shadow-[#fbbf24]/20";
            }
          }

          return (
            <div key={stage.key} className={`relative group ${!hasCount ? "opacity-50" : ""}`}>
              {/* Timeline Circle Node */}
              <div className={`absolute -left-6 md:-left-10 -translate-x-1/2 w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold text-lg transition-all duration-300 ${nodeStyles} z-10 top-[6px] md:top-[14px]`}>
                {detail.panelCount}
              </div>

              {/* Stage Info Card */}
              <div className={`p-4 md:p-6 rounded-xl border transition-all duration-300 ${
                hasCount 
                  ? "bg-neutral-900/40 border-neutral-800 shadow-md" 
                  : "bg-neutral-900/10 border-neutral-900/20"
              }`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="space-y-1">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      {stage.title}
                      {hasCount && !isReadyToGo && (
                        <span className="w-2 h-2 rounded-full bg-[#fbbf24] animate-pulse" />
                      )}
                      {hasCount && isReadyToGo && (
                        <span className="w-2 h-2 rounded-full bg-[#4fd1c5] animate-pulse" />
                      )}
                    </h3>
                    <p className="text-sm text-neutral-400">{stage.description}</p>
                  </div>
                </div>


              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
